import React from 'react';
import {
  Users,
  BrainCircuit,
  Target,
  TrendingUp,
  PieChart,
  DollarSign,
  MessageCircle,
  Lightbulb
} from 'lucide-react';

// Modern color palette using a consistent theme
const iconColor = "#0369a1";

// General advisory prompts (when no portfolio is selected)
export const generalPrompts = [
  {
    key: 'client_communication',
    icon: <MessageCircle size={20} color={iconColor} />,
    label: <span className={'font-medium'}>Client Communication</span>,
    description: (
      <span className={'text-sm text-gray-700'}>
        Get insights on effective client engagement strategies
      </span>
    ),
    prompt: "Help me understand what communication strategies are working for financial advisors across different client segments, especially with mid to high net worth individuals. What are the best practices for maintaining strong client relationships and addressing their concerns in today's market environment?"
  },
  {
    key: 'practice_growth',
    icon: <TrendingUp size={20} color={iconColor} />,
    label: <span className={'font-medium'}>Practice Growth</span>,
    description: (
      <span className={'text-sm text-gray-700'}>
        Strategies for expanding your advisory practice
      </span>
    ),
    prompt: "What are effective strategies for growing an advisory practice in today's environment? How can I identify and attract ideal clients while maintaining quality service for existing relationships?"
  },
  {
    key: 'wealth_planning',
    icon: <BrainCircuit size={20} color={iconColor} />,
    label: <span className={'font-medium'}>Wealth Planning Insights</span>,
    description: (
      <span className={'text-sm text-gray-700'}>
        Advanced planning strategies for high-net-worth clients
      </span>
    ),
    prompt: "What are some sophisticated wealth planning strategies that resonate with high-net-worth clients? How can I position advanced concepts like tax efficiency, estate planning, and wealth transfer in client conversations?"
  },
  {
    key: 'client_acquisition',
    icon: <Users size={20} color={iconColor} />,
    label: <span className={'font-medium'}>Client Acquisition</span>,
    description: (
      <span className={'text-sm text-gray-700'}>
        Tips for attracting and onboarding new clients
      </span>
    ),
    prompt: "What are successful advisors doing to attract and onboard new clients in today's market? How can I differentiate my practice and create a compelling value proposition for prospective clients?"
  }
];

// Portfolio-specific prompts
export const portfolioPrompts = [
  {
    key: 'portfolio_performance',
    icon: <TrendingUp size={20} color={iconColor} />,
    label: <span className={'font-medium'}>Portfolio Performance Analysis</span>,
    description: (
      <span className={'text-sm text-gray-700'}>
        Get a detailed breakdown of your portfolio's performance
      </span>
    ),
    prompt: "Analyze my portfolio's performance in detail. How have my investments performed relative to their benchmarks? What are the key drivers of returns?"
  },
  {
    key: 'asset_allocation',
    icon: <PieChart size={20} color={iconColor} />,
    label: <span className={'font-medium'}>Asset Allocation Review</span>,
    description: (
      <span className={'text-sm text-gray-700'}>
        Review current asset allocation and suggest potential adjustments
      </span>
    ),
    prompt: "Review my current asset allocation. Is it aligned with my investment goals? Are there any concentration risks or areas that need rebalancing?"
  },
  {
    key: 'risk_analysis',
    icon: <Target size={20} color={iconColor} />,
    label: <span className={'font-medium'}>Risk Analysis</span>,
    description: (
      <span className={'text-sm text-gray-700'}>
        Evaluate portfolio risks and exposure
      </span>
    ),
    prompt: "What are the main risks in my portfolio? Analyze factors like sector concentration, geographic exposure, and market cap distribution."
  },
  {
    key: 'income_analysis',
    icon: <DollarSign size={20} color={iconColor} />,
    label: <span className={'font-medium'}>Income & Yield Analysis</span>,
    description: (
      <span className={'text-sm text-gray-700'}>
        Review dividend and income potential
      </span>
    ),
    prompt: "Analyze the income-generating potential of my portfolio. What's the current yield? Are there opportunities to enhance income while maintaining risk levels?"
  }
];

// Function to get appropriate prompts based on context
export const getContextPrompts = (selectedPortfolioId) => {
  return selectedPortfolioId ? portfolioPrompts : generalPrompts;
};